import { Badge, Box, Button, Flex, HStack, Text, VStack } from "@chakra-ui/react";
import type { FailedOrder } from "@shared/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type React from "react";

import { formatShortDateTime } from "~/helpers/date.helper";

interface Props {
  order: FailedOrder;
  showDetails: (order: FailedOrder) => void;
}

const FailedOrderCard: React.FC<Props> = ({ order, showDetails }) => {
  const queryClient = useQueryClient();

  const toggleReviewedMutation = useMutation({
    mutationFn: async (newValue: boolean) => {
      const response = await fetch("/api/orders/toggle-reviewed", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ id: order.id, reviewed: newValue })
      });
      if (!response.ok) {
        alert("Failed to toggle reviewed status");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["failed-orders"] });
    }
  });

  const clearFailedOrderMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/orders/clear-failed-order/${order.id}`, {
        method: "POST"
      });
      if (!response.ok) {
        alert("Failed to clear failed order");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["failed-orders"] });
    }
  });

  const buildNameValueLine = (name: string, value: string) => {
    return (
      <Text>
        <Text fontWeight={600} as="span">
          {name}:{" "}
        </Text>
        {value}
      </Text>
    );
  };

  const toggleReviewed = (newValue: boolean) => {
    toggleReviewedMutation.mutate(newValue);
  };

  function confirmClearFailedOrder(): void {
    const shouldContinue = confirm(
      "Are you sure you want to clear this failed order?\nIt will be retried on the next scheduled sync."
    );
    if (shouldContinue) {
      clearFailedOrderMutation.mutate();
    }
  }

  return (
    <Box borderWidth="1px" borderRadius="lg" overflow="hidden" p={4} bg="white">
      <Flex flexDir={{ base: "column", lg: "row" }} gap={[0, 0, 6]}>
        <VStack align="start" spacing={1} flex={1}>
          <HStack>
            <Text fontWeight="bold">Order Number: {order.orderNumber}</Text>
          </HStack>

          {buildNameValueLine("Order ID", order.id)}
          {buildNameValueLine("Error", order.errorMsg)}
          {buildNameValueLine("Synced On", formatShortDateTime(new Date(order.syncedOn)))}
          <Button onClick={() => showDetails(order)}>Show Details</Button>
        </VStack>
        <VStack align="start" spacing={1} flex={1}>
          <Badge colorScheme={order.reviewed ? "green" : "red"}>{order.reviewed ? "Reviewed" : "Not Reviewed"}</Badge>
          <Button colorScheme="green" onClick={() => confirmClearFailedOrder()}>
            Retry on next sync
          </Button>
          <Button onClick={() => toggleReviewed(!order.reviewed)} isLoading={toggleReviewedMutation.isPending}>
            Mark as {order.reviewed ? "Unreviewed" : "Reviewed"}
          </Button>
        </VStack>
      </Flex>
    </Box>
  );
};

export default FailedOrderCard;
